import React, { forwardRef } from 'react';
import Card from '../Card';
import AgGridWrapper from '../AgGrid';

/**
 * AgTableCard
 *
 * Standardized Card container for AG-Grid tables implementing the benchmark UI from `experience/manage-experiences`:
 *
 * 1. Enclosing Card:
 *    - `<Card padding="none" className="overflow-hidden border border-border-table shadow-card">`
 * 2. Conditional Card Header / Toolbar (`header` / `toolbarLeft` / `toolbarRight`):
 *    - ONLY rendered when controls are provided.
 *    - Accommodates any custom components (Search, Forms, Select, Toggle, SegmentedControl, DateRangePicker)
 *      without altering their custom logic.
 *    - Does NOT inject unrequested search inputs, dropdowns, or filters when not provided.
 * 3. Integrated AG-Grid:
 *    - Renders AG-Grid inside a standard `<div className="p-5">` container.
 *    - Handles loading via AG-Grid's built-in overlay (`loading={loading}`) so the card and header remain mounted and stable.
 *    - Preserves all custom logic: sorting, filtering, selection, cell renderers, and callbacks.
 *    - Passes ONLY explicitly provided grid props to AgGridWrapper (never injecting `undefined` keys which could override AgGridWrapper defaults).
 *    - If `children` is provided alongside grid props, it renders alongside AG-Grid (e.g. context menus, popups).
 *    - If no grid props are provided, `children` acts as a full escape hatch for custom table content.
 * 4. Card Footer (`footer`):
 *    - Optional bottom slot with standard styling.
 */
const AgTableCard = forwardRef(({
  // Card Header / Toolbar slots
  header,
  toolbar,
  toolbarLeft,
  toolbarRight,
  headerClassName = '',

  // Card container styling & props
  className = '',
  padding = 'none',
  variant = 'default',
  cardRef,

  // Table container styling
  tableContainerClassName = 'p-5',

  // Card Footer
  footer,

  // Grid Ref & optional bundled grid props
  gridRef,
  gridProps,

  // Children (escape hatch or extra overlays like context menus)
  children,

  // All other props are forwarded directly to AgGridWrapper
  ...restGridProps
}, ref) => {
  // Determine if a card header should be rendered
  const hasHeader = Boolean(header || toolbar || toolbarLeft || toolbarRight);

  const renderHeader = () => {
    if (!hasHeader) return null;

    // Direct custom header takes precedence
    if (header || toolbar) {
      return (
        <div className={`p-4 border-b border-border-table bg-surface-alt flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${headerClassName}`}>
          {header || toolbar}
        </div>
      );
    }

    // Two-sided toolbar layout
    return (
      <div className={`p-4 border-b border-border-table bg-surface-alt flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${headerClassName}`}>
        {toolbarLeft ? (
          <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
            {toolbarLeft}
          </div>
        ) : <div />}

        {toolbarRight && (
          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            {toolbarRight}
          </div>
        )}
      </div>
    );
  };

  // Merge forwarded grid props with optional gridProps bundle
  const mergedGridProps = {
    ...restGridProps,
    ...gridProps,
  };

  // Check if grid props are provided
  const hasGridProps = Boolean(
    mergedGridProps.tableId ||
    mergedGridProps.rowData !== undefined ||
    mergedGridProps.columnDefs !== undefined ||
    (gridProps && Object.keys(gridProps).length > 0)
  );

  return (
    <Card
      ref={cardRef || ref}
      padding={padding}
      variant={variant}
      className={`overflow-hidden border border-border-table shadow-card ${className}`}
    >
      {/* 1. Card Header Toolbar (only if provided) */}
      {renderHeader()}

      {/* 2. Table Container */}
      <div className={tableContainerClassName}>
        {hasGridProps ? (
          <>
            <AgGridWrapper ref={gridRef} {...mergedGridProps} />
            {children}
          </>
        ) : (
          children
        )}
      </div>

      {/* 3. Card Footer (optional) */}
      {footer && (
        <div className="border-t border-border-table p-4 bg-surface-alt">
          {footer}
        </div>
      )}
    </Card>
  );
});

AgTableCard.displayName = 'AgTableCard';

export default AgTableCard;
